/**
 * ArchivedApplicationsTable — Table view for archived (terminal) applications
 * Shows settled, declined, withdrawn, and lapsed applications with re-activation.
 */

import { useState } from 'react'
import { RotateCcw } from 'lucide-react'
import { STAGE_DISPLAY, TERMINAL_STAGES, ALL_STAGES } from '../constants/applicationStages'
import { reactivateApplication, type Application } from '../api/brokerApi'

const ACTIVE_STAGES = ALL_STAGES.filter(
  s => !TERMINAL_STAGES.includes(s) && s !== 'on_hold'
)

const DEFAULT_REACTIVATION_STAGE: Record<string, string> = {
  settled: 'new_lead',
  declined: 'loan_research_in_progress',
  withdrawn_by_client: 'initial_consultation_scheduled',
  withdrawn_by_broker: 'initial_consultation_scheduled',
  lapsed: 'initial_consultation_scheduled',
}

interface Props {
  applications: Application[]
  loading: boolean
  token: string
  onReactivated: () => void
  onAppClick: (app: Application) => void
}

export default function ArchivedApplicationsTable({ applications, loading, token, onReactivated, onAppClick }: Props) {
  const [reactivatingId, setReactivatingId] = useState<string | null>(null)
  const [reactivateStage, setReactivateStage] = useState('')
  const [reactivateNotes, setReactivateNotes] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const handleReactivate = async (app: Application) => {
    if (!reactivateStage) return
    setSubmitting(true)
    try {
      await reactivateApplication(token, app.id, reactivateStage, reactivateNotes || undefined)
      setReactivatingId(null)
      setReactivateStage('')
      setReactivateNotes('')
      onReactivated()
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to re-activate')
    } finally {
      setSubmitting(false)
    }
  }

  const outcomeColor = (outcome: string | null) => {
    switch (outcome) {
      case 'approved': return 'bg-green-100 text-green-800'
      case 'declined': return 'bg-red-100 text-red-800'
      case 'withdrawn': return 'bg-yellow-100 text-yellow-800'
      default: return 'bg-gray-100 text-gray-600'
    }
  }

  const formatDate = (d: string | null) => {
    if (!d) return '—'
    return new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
  }

  if (loading) {
    return <p className="text-sm text-gray-500 py-8 text-center">Loading archived applications...</p>
  }

  if (applications.length === 0) {
    return <p className="text-sm text-gray-500 py-8 text-center">No archived applications</p>
  }

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 sticky top-0">
            <tr>
              <th className="text-left px-3 py-2 font-medium text-gray-600">Customer</th>
              <th className="text-left px-3 py-2 font-medium text-gray-600">Lender</th>
              <th className="text-left px-3 py-2 font-medium text-gray-600">Loan Amount</th>
              <th className="text-left px-3 py-2 font-medium text-gray-600">Outcome</th>
              <th className="text-left px-3 py-2 font-medium text-gray-600">Final Stage</th>
              <th className="text-left px-3 py-2 font-medium text-gray-600">Closed Date</th>
              <th className="text-left px-3 py-2 font-medium text-gray-600">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {applications.map(app => (
              <tr key={app.id} className="hover:bg-gray-50">
                <td className="px-3 py-2.5">
                  <button
                    onClick={() => onAppClick(app)}
                    className="text-blue-600 hover:underline font-medium"
                  >
                    {app.customer_name || 'Unknown'}
                  </button>
                </td>
                <td className="px-3 py-2.5 text-gray-700">{app.lender_name || '—'}</td>
                <td className="px-3 py-2.5 text-gray-700">
                  {app.loan_amount ? `$${app.loan_amount.toLocaleString()}` : '—'}
                </td>
                <td className="px-3 py-2.5">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${outcomeColor(app.outcome)}`}>
                    {app.outcome || app.stage.replace(/_/g, ' ')}
                  </span>
                </td>
                <td className="px-3 py-2.5 text-gray-600 text-xs">
                  {STAGE_DISPLAY[app.stage] || app.stage}
                </td>
                <td className="px-3 py-2.5 text-gray-600 text-xs">
                  {formatDate(app.settled_at || app.stage_updated_at)}
                </td>
                <td className="px-3 py-2.5">
                  <button
                    onClick={() => {
                      setReactivatingId(app.id)
                      setReactivateStage(DEFAULT_REACTIVATION_STAGE[app.stage] || 'new_lead')
                      setReactivateNotes('')
                    }}
                    className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium"
                  >
                    <RotateCcw className="w-3.5 h-3.5" /> Re-activate
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Re-activation Modal */}
      {reactivatingId && (
        <>
          <div className="fixed inset-0 bg-black/30 z-50" onClick={() => setReactivatingId(null)} />
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Re-activate Application</h3>
              <p className="text-sm text-gray-600 mb-4">
                This will move the application back to an active stage on the kanban board.
              </p>
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-medium text-gray-700 block mb-1">Target Stage</label>
                  <select
                    value={reactivateStage}
                    onChange={e => setReactivateStage(e.target.value)}
                    className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2"
                  >
                    {ACTIVE_STAGES.map(s => (
                      <option key={s} value={s}>{STAGE_DISPLAY[s] || s}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-700 block mb-1">Notes (optional)</label>
                  <input
                    value={reactivateNotes}
                    onChange={e => setReactivateNotes(e.target.value)}
                    placeholder="Reason for reactivation..."
                    className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2"
                  />
                </div>
              </div>
              <div className="flex gap-3 mt-5 justify-end">
                <button
                  onClick={() => setReactivatingId(null)}
                  className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    const app = applications.find(a => a.id === reactivatingId)
                    if (app) handleReactivate(app)
                  }}
                  disabled={submitting || !reactivateStage}
                  className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {submitting ? 'Re-activating...' : 'Re-activate'}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
